import React from 'react';
import { TableProps } from '@amzn/awsui-components-console/table';
import Link from '@amzn/awsui-components-console/link';

import { ActiveTranslationJob, HistoricalTranslationJob } from '../types';
import {
  formatEpochMillisDate,
  computeTurnaround,
} from 'src/common/utils/dateFormatters';
import {
  TranslationStatusIndicator,
  getTranslationStatusLabel,
} from 'src/common/translationStatus';

/**
 * Build href for a string group detail page.
 *
 * TODO: return `/${namespaceId}/${stringGroupId}` once the string group detail
 * route exists in App.tsx. Today there is no 2-segment route, so a real path
 * would fall through to the `*` catch-all and redirect to the landing page —
 * '#' is the same placeholder href SearchResultsTable uses.
 */
const buildStringGroupHref = (_item: {
  namespaceId: string;
  stringGroupId: string;
}): string => '#';

/**
 * String group cell link, inert until the detail route exists.
 *
 * The href is a '#' placeholder, so the default action is prevented: following
 * it would otherwise append '#' to the URL and jump the page to the top, which
 * looks like a broken link rather than an unimplemented one. The href is still
 * rendered so the cell is a real link for middle-click and screen readers, and
 * so wiring the route later is a change in one place.
 *
 * TODO: when the route lands, take `navigate` and follow the namespace column's
 * onFollow + navigate pattern instead of preventing the default outright.
 */
const StringGroupLink: React.FC<{
  item: { namespaceId: string; stringGroupId: string };
}> = ({ item }) => (
  <Link href={buildStringGroupHref(item)} onFollow={(e) => e.preventDefault()}>
    {item.stringGroupId}
  </Link>
);

/** Cells hold "V12", so size to the header rather than an equal share of spare width. */
const VERSION_COLUMN_WIDTH = 110;

/** Fits the header plus its sort control: narrowing Version truncated it to "Ingestion So…". */
const INGESTION_SOURCE_COLUMN_WIDTH = 190;

/*
 * Sorting comparators are module-level constants because Cloudscape decides
 * which header is sorted by comparator IDENTITY — a comparator rebuilt per
 * render would never match the active sorting column.
 */

/** Missing timestamps compare as 0, clustering NA rows at one end of the sort. */
const epochMillis = (value: string | number | null | undefined): number =>
  value ? Number(value) : 0;

const compareVersion = (
  a: { stringGroupVersion?: string | null },
  b: { stringGroupVersion?: string | null },
): number =>
  Number(a.stringGroupVersion ?? 0) - Number(b.stringGroupVersion ?? 0);

export const compareSentForTranslation = (
  a: { sentForTranslationDate?: string | null },
  b: { sentForTranslationDate?: string | null },
): number =>
  epochMillis(a.sentForTranslationDate) - epochMillis(b.sentForTranslationDate);

const compareDueDate = (
  a: { dueDate?: string | null },
  b: { dueDate?: string | null },
): number => epochMillis(a.dueDate) - epochMillis(b.dueDate);

const compareDeliveredOn = (
  a: { deliveredOnDate?: string | null },
  b: { deliveredOnDate?: string | null },
): number => epochMillis(a.deliveredOnDate) - epochMillis(b.deliveredOnDate);

/** Turnaround in raw millis — never the formatted "N days" cell text. */
const turnaroundMillis = (job: {
  sentForTranslationDate?: string | null;
  deliveredOnDate?: string | null;
}): number =>
  job.deliveredOnDate
    ? epochMillis(job.deliveredOnDate) - epochMillis(job.sentForTranslationDate)
    : 0;

const compareTurnaround = (
  a: Parameters<typeof turnaroundMillis>[0],
  b: Parameters<typeof turnaroundMillis>[0],
): number => turnaroundMillis(a) - turnaroundMillis(b);

/** Orders by the VISIBLE label, not the raw token, so sorting matches the column. */
const compareStatus = (
  a: { translationStatus?: string | null },
  b: { translationStatus?: string | null },
): number =>
  getTranslationStatusLabel(a.translationStatus ?? '').localeCompare(
    getTranslationStatusLabel(b.translationStatus ?? ''),
  );

/** Oldest sent first, for BOTH tables: the longest-waiting job is the closest to
 *  missing its due date, so it belongs on top rather than the last page. */
export const OLDEST_FIRST_SORTING_STATE = {
  sortingColumn: { sortingComparator: compareSentForTranslation },
  isDescending: false,
};

/**
 * Creates active job column definitions with SPA-aware links.
 * Uses onFollow + preventDefault to avoid full page reloads and
 * Midway/Cognito re-auth round-trips.
 */
export const createActiveColumnDefinitions = (
  navigate: (path: string) => void,
): TableProps.ColumnDefinition<ActiveTranslationJob>[] => [
  {
    id: 'stringGroup',
    header: 'String Group',
    sortingField: 'stringGroupId',
    cell: (item) => <StringGroupLink item={item} />,
  },
  {
    id: 'version',
    header: 'Version',
    // Cells are short ("V12"), so the column only needs to fit its own header.
    width: VERSION_COLUMN_WIDTH,
    sortingComparator: compareVersion,
    cell: (item) => `V${item.stringGroupVersion}`,
  },
  {
    id: 'dueDate',
    header: 'Due Date',
    sortingComparator: compareDueDate,
    cell: (item) => formatEpochMillisDate(item.dueDate),
  },
  {
    id: 'sentForTranslation',
    header: 'Sent on',
    sortingComparator: compareSentForTranslation,
    cell: (item) => formatEpochMillisDate(item.sentForTranslationDate),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => (
      <TranslationStatusIndicator status={item.translationStatus} />
    ),
  },
  {
    id: 'trackingId',
    header: 'Tracking ID',
    cell: (item) => item.trackingId,
  },
  {
    id: 'clientId',
    header: 'Ingestion Source',
    width: INGESTION_SOURCE_COLUMN_WIDTH,
    sortingField: 'clientId',
    cell: (item) => item.clientId,
  },
  {
    id: 'namespace',
    header: 'Namespace',
    cell: (item) => (
      <Link
        href={`/${item.namespaceId}`}
        onFollow={(e) => {
          e.preventDefault();
          navigate(`/${item.namespaceId}`);
        }}
      >
        {item.namespaceId}
      </Link>
    ),
  },
];

/**
 * Creates historical job column definitions with SPA-aware links.
 */
export const createHistoricalColumnDefinitions = (
  navigate: (path: string) => void,
): TableProps.ColumnDefinition<HistoricalTranslationJob>[] => [
  {
    id: 'stringGroup',
    header: 'String Group',
    sortingField: 'stringGroupId',
    cell: (item) => <StringGroupLink item={item} />,
  },
  {
    id: 'version',
    header: 'Version',
    width: VERSION_COLUMN_WIDTH,
    sortingComparator: compareVersion,
    cell: (item) => `V${item.stringGroupVersion}`,
  },
  {
    id: 'deliveredOn',
    header: 'Delivered On',
    sortingComparator: compareDeliveredOn,
    /*
     * 'NA' is keyed off the absence of `deliveredOnDate`, not `status ===
     * CANCELLED`: the backend sets that date only for COMPLETE jobs, so absence
     * covers cancelled and failed jobs alike.
     */
    cell: (item) => formatEpochMillisDate(item.deliveredOnDate),
  },
  {
    id: 'sentOn',
    header: 'Sent on',
    sortingComparator: compareSentForTranslation,
    cell: (item) => formatEpochMillisDate(item.sentForTranslationDate),
  },
  {
    id: 'turnaround',
    header: 'TAT',
    sortingComparator: compareTurnaround,
    cell: (item) =>
      computeTurnaround(item.sentForTranslationDate, item.deliveredOnDate),
  },
  // Replaces the 'Job Failure' / 'Cancelled Job' Yes-No pair: a job has exactly one
  // terminal state, so it reads as one value, via the badge the active table uses.
  {
    id: 'status',
    header: 'Status',
    sortingComparator: compareStatus,
    cell: (item) => (
      <TranslationStatusIndicator status={item.translationStatus} />
    ),
  },
  {
    id: 'trackingId',
    header: 'Tracking ID',
    cell: (item) => item.trackingId,
  },
  {
    id: 'clientId',
    header: 'Ingestion Source',
    width: INGESTION_SOURCE_COLUMN_WIDTH,
    sortingField: 'clientId',
    cell: (item) => item.clientId,
  },
  {
    id: 'namespace',
    header: 'Namespace',
    cell: (item) => (
      <Link
        href={`/${item.namespaceId}`}
        onFollow={(e) => {
          e.preventDefault();
          navigate(`/${item.namespaceId}`);
        }}
      >
        {item.namespaceId}
      </Link>
    ),
  },
];
